import { useState } from "react";
import { Box, Grid, Typography } from "@mui/joy";
import { useFormContext } from "react-hook-form";
import { MapPin } from "lucide-react";

import { Button, PhoneField, TextField } from "../../../../../shared";
import type { NewProjectQuickFormValues } from "../../../../../libs/form-validations/new-project-quick";
import type { PickedAddress } from "../../../../../utils/geocode";
import AddressMapPicker from "./AddressMapPicker";

export default function CustomerStep() {
    const { setValue } = useFormContext<NewProjectQuickFormValues>();
    const [mapOpen, setMapOpen] = useState(false);

    const applyAddress = (address: PickedAddress) => {
        setValue("customerStreetAddress", address.street, {
            shouldDirty: true,
            shouldValidate: true,
        });
        setValue("customerCity", address.city, {
            shouldDirty: true,
            shouldValidate: true,
        });
        setValue("customerState", address.state, {
            shouldDirty: true,
            shouldValidate: true,
        });
        setValue("customerZipCode", address.zip, {
            shouldDirty: true,
            shouldValidate: true,
        });
        if (address.county) {
            setValue("customerCountry", address.county, {
                shouldDirty: true,
                shouldValidate: true,
            });
        }
    };

    return (
        <>
            <Grid container spacing={2}>
                <Grid xs={12}>
                    <TextField
                        name="customerName"
                        label="Customer Name"
                        helperText="Name of the customer"
                        placeholder="e.g., Jane Doe"
                    />
                </Grid>
                <Grid xs={12}>
                    <PhoneField
                        name="customerPhoneNumber"
                        countryName="customerPhoneCountry"
                        label="Phone Number"
                        helperText="Select country, then enter the number"
                    />
                </Grid>
                <Grid xs={12}>
                    <TextField
                        name="customerEmail"
                        label="Email (optional)"
                        helperText="Optional email address"
                        placeholder="e.g., jane@example.com"
                    />
                </Grid>

                <Grid xs={12}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                            mb: 0.5,
                        }}
                    >
                        <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                            Address
                        </Typography>
                        <Button
                            type="button"
                            size="sm"
                            variant="plain"
                            color="primary"
                            startDecorator={<MapPin size={14} />}
                            onClick={() => setMapOpen(true)}
                            sx={{ minHeight: 28, px: 0.75 }}
                        >
                            Pick on map
                        </Button>
                    </Box>
                </Grid>

                <Grid xs={12}>
                    <TextField
                        name="customerStreetAddress"
                        label="Street Address"
                        helperText="Street name and house/building number"
                        placeholder="e.g., 123 Main St"
                    />
                </Grid>
                <Grid xs={12} sm={5}>
                    <TextField
                        name="customerCity"
                        label="City"
                        helperText="City"
                        placeholder="e.g., Denver"
                    />
                </Grid>
                <Grid xs={4} sm={2}>
                    <TextField
                        name="customerState"
                        label="State"
                        helperText="State"
                        placeholder="e.g., CO"
                    />
                </Grid>
                <Grid xs={8} sm={5}>
                    <TextField
                        name="customerZipCode"
                        label="ZIP Code"
                        helperText="5-digit ZIP"
                        placeholder="e.g., 80202"
                    />
                </Grid>
                <Grid xs={12}>
                    <TextField
                        name="customerCountry"
                        label="County"
                        helperText="County where the customer is located"
                        placeholder="e.g., Denver County"
                    />
                </Grid>
            </Grid>

            <AddressMapPicker
                open={mapOpen}
                onClose={() => setMapOpen(false)}
                onConfirm={applyAddress}
            />
        </>
    );
}
